import { Center, CreateCenterDto, UpdateCenterDto } from './types';

export class CenterService {
  private getBackendBaseURL(): string {
    const w: any = window as any;
    return w._BACKEND_BASE_URL || import.meta.env.VITE_BACKEND_URL || 'http://localhost:3001';
  }

  private getAuthHeader(): Record<string, string> {
    const raw = (typeof window !== 'undefined' ? localStorage.getItem('backend_access_token') : '') || '';
    const token = raw.replace(/^"+|"+$/g, '').trim();
    const looksLikeJwt = token && token.split('.').length === 3;
    return looksLikeJwt ? { Authorization: `Bearer ${token}` } : {};
  }

  async getAllCenters(): Promise<Center[]> {
    const res = await fetch(`${this.getBackendBaseURL()}/centers`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
        ...this.getAuthHeader(),
      },
      credentials: 'include',
    });

    if (!res.ok) {
      if (res.status === 401) {
        throw new Error('Your session has expired. Please log in again.');
      }
      const text = await res.text();
      throw new Error(text || 'Failed to fetch centers');
    }

    const data = await res.json();
    return Array.isArray(data) ? data : [];
  }

  async getCenterById(id: string): Promise<Center | null> {
    const res = await fetch(`${this.getBackendBaseURL()}/centers/${encodeURIComponent(id)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
        ...this.getAuthHeader(),
      },
      credentials: 'include',
    });

    if (!res.ok) {
      if (res.status === 401) {
        throw new Error('Your session has expired. Please log in again.');
      }
      if (res.status === 404) return null;
      const text = await res.text();
      throw new Error(text || 'Failed to fetch center');
    }

    return await res.json();
  }

  async getCenterByManagerId(managerId: string): Promise<Center | null> {
    const res = await fetch(`${this.getBackendBaseURL()}/centers/manager/${encodeURIComponent(managerId)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
        ...this.getAuthHeader(),
      },
      credentials: 'include',
    });

    if (!res.ok) {
      if (res.status === 401) {
        throw new Error('Your session has expired. Please log in again.');
      }
      if (res.status === 404) return null;
      const text = await res.text();
      throw new Error(text || 'Failed to fetch center');
    }

    return await res.json();
  }

  async createCenter(createCenterDto: CreateCenterDto): Promise<Center> {
    const res = await fetch(`${this.getBackendBaseURL()}/centers`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
        ...this.getAuthHeader(),
      },
      credentials: 'include',
      body: JSON.stringify(createCenterDto),
    });

    if (!res.ok) {
      if (res.status === 401) {
        throw new Error('Your session has expired. Please log in again.');
      }
      const text = await res.text();
      throw new Error(text || 'Failed to create center');
    }

    return await res.json();
  }

  async updateCenter(id: string, updateCenterDto: UpdateCenterDto): Promise<Center> {
    const res = await fetch(`${this.getBackendBaseURL()}/centers/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
        ...this.getAuthHeader(),
      },
      credentials: 'include',
      body: JSON.stringify(updateCenterDto),
    });

    if (!res.ok) {
      if (res.status === 401) {
        throw new Error('Your session has expired. Please log in again.');
      }
      const text = await res.text();
      throw new Error(text || 'Failed to update center');
    }

    return await res.json();
  }

  async deleteCenter(id: string): Promise<boolean> {
    const res = await fetch(`${this.getBackendBaseURL()}/centers/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
        ...this.getAuthHeader(),
      },
      credentials: 'include',
    });

    if (!res.ok) {
      if (res.status === 401) {
        throw new Error('Your session has expired. Please log in again.');
      }
      const text = await res.text();
      throw new Error(text || 'Failed to delete center');
    }

    return true;
  }

  async getCenterUsers(centerId: string): Promise<any[]> {
    const res = await fetch(`${this.getBackendBaseURL()}/centers/${encodeURIComponent(centerId)}/users`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
        ...this.getAuthHeader(),
      },
      credentials: 'include',
    });

    if (!res.ok) {
      if (res.status === 401) {
        throw new Error('Your session has expired. Please log in again.');
      }
      const text = await res.text();
      throw new Error(text || 'Failed to fetch center users');
    }

    return await res.json();
  }
}

